import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForcePasswordChange, { ResetPassword } from "@/pages/PasswordChange";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import { Customers, CustomerDetail } from "@/pages/Customers";
import Products from "@/pages/Products";
import Quotations from "@/pages/Quotations";
import QuotationBuilder from "@/pages/QuotationBuilder";
import QuotationDetail from "@/pages/QuotationDetail";
import Inventory from "@/pages/Inventory";
import QuotationTemplates from "@/pages/QuotationTemplates";
import RecordAdmin from "@/pages/RecordAdmin";
import PaymentReminders from "@/pages/PaymentReminders";
import GstReports from "@/pages/GstReports";
import Collections from "@/pages/Collections";
import CollectionsPro from "@/pages/CollectionsPro";
import Approvals from "@/pages/Approvals";
import SystemHealth from "@/pages/SystemHealth";
import LeadRoi from "@/pages/LeadRoi";
import EwaySettings from "@/pages/EwaySettings";
import ContactImport from "@/pages/ContactImport";
import CaExport from "@/pages/CaExport";
import Purchase from "@/pages/Purchase";
import AIRequests, { AIAssistant } from "@/pages/AIRequests";
import { Expenses, Invoices, Payments, Receivables } from "@/pages/Finance";
import InvoiceEditor from "@/pages/InvoiceEditor";
import Dispatch from "@/pages/Dispatch";
import ServiceWarranty from "@/pages/ServiceWarranty";
import Work from "@/pages/Work";
import Automation from "@/pages/Automation";
import HR from "@/pages/HR";
import Payroll from "@/pages/Payroll";
import MyHub from "@/pages/MyHub";
import DataVault from "@/pages/DataVault";
import Manufacturing from "@/pages/Manufacturing";
import { Employees, Projects, Reports, SalesOrders, Suppliers } from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Permissions from "@/pages/Permissions";
import Kiosk from "@/pages/Kiosk";
import Performance from "@/pages/Performance";
import EmployeeApp from "@/pages/EmployeeApp";
import LiveTracking from "@/pages/LiveTracking";
import AuthCallback from "@/pages/AuthCallback";
import "@/App.css";

function Shell() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  if (loading)
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!user)
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );

  if (user.must_change_password) return <ForcePasswordChange />;

  // Basic employees only ever get the Employee App — never the ERP shell.
  if (user.role === "employee")
    return (
      <Routes>
        <Route path="/employee" element={<EmployeeApp />} />
        <Route path="*" element={<Navigate to="/employee" replace />} />
      </Routes>
    );

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/products" element={<Products />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/quotations/template-editor" element={<QuotationTemplates />} />
        <Route path="/quotations/new" element={<QuotationBuilder />} />
        <Route path="/quotations/:id/edit" element={<QuotationBuilder />} />
        <Route path="/quotations/:id" element={<QuotationDetail />} />
        <Route path="/orders" element={<SalesOrders />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/receivables" element={<Receivables />} />
        <Route path="/reminders" element={<PaymentReminders />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/ai-collections" element={<CollectionsPro />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/system-health" element={<SystemHealth />} />
        <Route path="/eway-settings" element={<EwaySettings />} />
        <Route path="/lead-roi" element={<LeadRoi />} />        <Route path="/contact-import" element={<ContactImport />} />
        <Route path="/ca-export" element={<CaExport />} />
        <Route path="/gst-reports" element={<GstReports />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/invoices/:id/edit" element={<InvoiceEditor />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/service" element={<ServiceWarranty />} />
        <Route path="/work" element={<Work />} />
        <Route path="/automation" element={<Automation />} />
        <Route path="/hr" element={<HR />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/my" element={<MyHub />} />
        <Route path="/data-vault" element={<DataVault />} />
        <Route path="/manufacturing" element={<Manufacturing />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/purchase" element={<Purchase />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/ai-requests" element={<AIRequests />} />
        <Route path="/assistant" element={<AIAssistant />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin/records" element={<RecordAdmin />} />
        <Route path="/permissions" element={<Permissions />} />
        <Route path="/kiosk" element={<Kiosk />} />
        <Route path="/tracking" element={<LiveTracking />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/employee" element={<EmployeeApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrandingProvider>
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </BrandingProvider>
  );
}
